import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  Backup,
  BackupSiteReply,
  CreateResponseJSON,
  GetResponseJSON,
  GetTotalResponseJSON,
  ListResponseJSON,
  RestoreResponseJSON,
  StagingFromBackupReply,
} from './backup.model';
import { IAMTokenService } from '../iam-token.service';
import { environment } from '../../../globals';

const SITE_URL_V1 = '/api/v1/site/';
const SITE_URL_V2 = '/api/v2/site/';

@Injectable()
export class BackupApiService {
  private headers = new HttpHeaders().set('Content-Type', 'application/json');
  private readonly host: string;

  constructor(private http: HttpClient, private iamTokenService: IAMTokenService) {
    this.host =
      environment !== 'prod' && environment !== 'production'
        ? 'website-pro-demo-backup-manager.vendasta-internal.com'
        : 'website-pro-prod-backup-manager.vendasta-internal.com';
  }

  get(siteId: string): Observable<string[]> {
    const url = `${SITE_URL_V1}${siteId}/backup/list`;
    return this.http.get(url).pipe(
      map((res: GetResponseJSON) => res.data.backup_names),
      catchError((err) => throwError(err)),
    );
  }

  restore(siteId: string, backupDateTime: string): Observable<string> {
    const url = `${SITE_URL_V1}${siteId}/backup/${backupDateTime}/restore`;
    return this.http.post(url, {}).pipe(
      map((res: RestoreResponseJSON) => res.data.job_id),
      catchError((err) => throwError(err)),
    );
  }

  list(siteId: string, offset: number, pageSize: number, isStaging?: boolean): Observable<Backup[]> {
    const url = `${SITE_URL_V2}${siteId}/backup/list/`;
    pageSize = pageSize ? pageSize + 1 : 11; //get one extra backup to use for comparing backup differences

    const params = new HttpParams({
      fromObject: {
        page_offset: offset.toString(),
        page_size: pageSize.toString(),
        is_staging: isStaging ? isStaging.toString() : 'false',
      },
    });
    return this.http.get(url, { params }).pipe(
      map((res: ListResponseJSON) => {
        const backups: Backup[] = [];
        for (const backupJson of res.data.backups) {
          backups.push(Backup.fromJSON(backupJson));
        }
        return backups;
      }),
      catchError((err) => throwError(err)),
    );
  }

  getTotalNumber(siteId: string, isStaging?: boolean): Observable<number> {
    const url = `${SITE_URL_V2}${siteId}/backup/count/`;
    const params = new HttpParams({
      fromObject: {
        is_staging: isStaging ? isStaging.toString() : 'false',
      },
    });
    return this.http.get(url, { params }).pipe(
      map((res: GetTotalResponseJSON) => res.data.total),
      catchError((err) => throwError(err)),
    );
  }

  create(siteId: string, description: string, isStaging?: boolean): Observable<BackupSiteReply> {
    const url = `${SITE_URL_V2}${siteId}/backup/create/`;
    const data = { description: description, is_staging: isStaging };
    return this.http.post(url, JSON.stringify(data), { headers: this.headers, withCredentials: true }).pipe(
      map((res: CreateResponseJSON) => BackupSiteReply.fromJSON(res.data)),
      catchError((err) => throwError(err)),
    );
  }

  createStagingFromBackup(siteId: string, timestamp: string): Observable<StagingFromBackupReply> {
    const url = this.hostWithScheme() + `${SITE_URL_V2}${siteId}/staging-from-backup/${timestamp}/`;
    return this.http.post(url, null, { headers: this.headers, withCredentials: true }).pipe(
      map((res: any) => {
        return StagingFromBackupReply.fromJSON(res.data);
      }),
    );
  }

  hostWithScheme(): string {
    const scheme = 'https://';
    return scheme + this.host;
  }
}
